import { ChatBubbleOutlineOutlined as ChatBubbleOutlineIcon } from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl } from "@ryde/controls";
import { useBookingsNavigation, useRidesNavigation } from "@ryde/redux";
import {
  BOOKINGS_URL,
  capitalize,
  getLastNavigation,
  LeftActivePageEnum,
  RideRoutes,
  RIDES_URL,
  RoutePaths,
  settingsDrawerOpenStateAtom,
  systemSettingsDtoAtom,
} from "@ryde/shared";
import { getMenuItemColor } from "@ryde/theme";

import {
  LeftBarButton,
  LeftBarButtonDrop,
  LeftBarNavigationStyled,
  SideBarLink,
  useLeftbarActivePaths,
} from "./parts";

export const LeftBarNavigation: FC = () => {
  const { t } = useTranslation();
  const setSettingDrawerOpenState = useSetRecoilState(
    settingsDrawerOpenStateAtom,
  );

  const { rideSearchGrid } = useRidesNavigation();
  const { bookingSearchGrid } = useBookingsNavigation();

  const rightBarToggleEvent: MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    setSettingDrawerOpenState(true);
  };

  const { leftActivePage } = useLeftbarActivePaths();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { showCourses, enableComplains } = systemSettingsDto?.metadata ?? {};
  const toRides = getLastNavigation(RIDES_URL);
  const toBookings = getLastNavigation(BOOKINGS_URL);

  return (
    <LeftBarNavigationStyled className="nav_ul">
      <AccessControl entity="ride" action="read">
        <SideBarLink
          to={toRides}
          queryParams={rideSearchGrid}
          data-e2e-id={"left-bar-button-rides"}
          tooltip={capitalize(t(`ride.plural`))}
        >
          <SVG_MUI.MenuBusSVG
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.Rides,
            )}
          />
        </SideBarLink>
      </AccessControl>

      <AccessControl entity="booking" action="read">
        <SideBarLink
          to={toBookings}
          queryParams={bookingSearchGrid}
          data-e2e-id={"left-bar-button-bookings"}
          tooltip={capitalize(t(`booking.plural`))}
        >
          <SVG_MUI.MenuCalendarSVG
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.Bookings,
            )}
          />
        </SideBarLink>
      </AccessControl>

      <AccessControl entity="ride" action="read">
        <SideBarLink
          to={RideRoutes.SEARCH_MAP}
          tooltip={capitalize(t(`general.LIVE_MAP`))}
        >
          <SVG_MUI.MenuMapSVG
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.LiveMap,
            )}
          />
        </SideBarLink>
      </AccessControl>

      <AccessControl entity="system" action="view_reports">
        <SideBarLink
          to={RoutePaths.REPORTS_INITIAL}
          tooltip={capitalize(t(`general.REPORTS`))}
        >
          <SVG_MUI.MenuSquarePollSVG
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.Reports,
            )}
          />
        </SideBarLink>
      </AccessControl>

      {showCourses && (
        <AccessControl entity="course" action="read">
          <SideBarLink
            to={RoutePaths.COURSES}
            tooltip={capitalize(t(`course.plural`))}
          >
            <SVG_MUI.CourseMenu4SVG
              color={getMenuItemColor(
                leftActivePage === LeftActivePageEnum.Courses,
              )}
            />
          </SideBarLink>
        </AccessControl>
      )}

      <AccessControl entity="passenger" action="read">
        <SideBarLink
          to={RoutePaths.PASSENGERS}
          tooltip={capitalize(t(`passenger.plural`))}
        >
          <SVG_MUI.PassengersIconSVG
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.Passengers,
            )}
          />
        </SideBarLink>
      </AccessControl>

      <AccessControl entity="produceButton" action="read">
        <SideBarLink
          to={RoutePaths.PRODUCE_BUTTONS}
          tooltip={capitalize(t(`produceButton.plural`))}
        >
          <SVG_MUI.ProductionSVG
            width={22}
            height={22}
            color={getMenuItemColor(
              leftActivePage === LeftActivePageEnum.ProduceButtons,
            )}
          />
        </SideBarLink>
      </AccessControl>

      {enableComplains && (
        <AccessControl entity="feedback" action="read">
          <SideBarLink
            to={RoutePaths.FEEDBACKS}
            tooltip={capitalize(t(`feedback.plural`))}
          >
            <ChatBubbleOutlineIcon
              className={clsx("svg-go-feedback-page", {
                active: leftActivePage === LeftActivePageEnum.Feedbacks,
              })}
            />
          </SideBarLink>
        </AccessControl>
      )}

      <LeftBarButton
        onMenuItemClick={rightBarToggleEvent}
        tooltip={capitalize(t(`general.OPEN_SETTINGS`))}
        data-e2e-id={"left-bar-button-settings"}
      >
        <SVG_MUI.MenuSlidersSVG
          color={getMenuItemColor(
            leftActivePage === LeftActivePageEnum.Settings,
          )}
        />
      </LeftBarButton>

      <LeftBarButtonDrop />
    </LeftBarNavigationStyled>
  );
};
